/**
 * HostA Global - Email verification result page (dashboard)
 * Styled to match sign-in / auth theme. Redirects: property owner -> sign-in, client -> client site login.
 * Messages follow website language (en/ar) via URL ?lang= or browser language.
 */

import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import apiRequest from "utils/api";

// Images – same as sign-in
import logo from "assets/images/logo-ct.png";

const REDIRECT_DELAY_SEC = 5;
const BRAND_GOLD_HEADER = "#b6925b";
const BRAND_BUTTON = "#b99160";
const BRAND_BUTTON_HOVER = "#a67d4d";

const VERIFY_TEXTS = {
  en: {
    headerTitle: "Email verification",
    verifying: "Verifying your email…",
    successClient: "Email verified successfully.",
    successOwner: "Your email has been verified successfully.",
    partnerDashboardSub: "You can now sign in to the partner dashboard.",
    websiteSub: "You can now sign in to your account on the website.",
    redirecting: "Redirecting in {count} second{s}…",
    goPartner: "Go to partner dashboard",
    goWebsite: "Go to website login",
    goSignIn: "Go to sign in",
    errorInvalidLink: "This link is invalid, expired, or has already been used.",
    errorMissingToken: "Verification link is invalid. Missing token.",
  },
  ar: {
    headerTitle: "التحقق من البريد الإلكتروني",
    verifying: "جاري التحقق من بريدك الإلكتروني…",
    successClient: "تم التحقق من البريد الإلكتروني بنجاح.",
    successOwner: "تم التحقق من بريدك الإلكتروني بنجاح.",
    partnerDashboardSub: "يمكنك الآن تسجيل الدخول إلى لوحة الشريك.",
    websiteSub: "يمكنك الآن تسجيل الدخول إلى حسابك على الموقع.",
    redirecting: "إعادة التوجيه خلال {count} ثانية…",
    goPartner: "الذهاب إلى لوحة الشريك",
    goWebsite: "الذهاب إلى تسجيل الدخول في الموقع",
    goSignIn: "الذهاب لتسجيل الدخول",
    errorInvalidLink: "هذا الرابط غير صالح أو منتهي الصلاحية أو تم استخدامه مسبقاً.",
    errorMissingToken: "رابط التحقق غير صالح. الرمز مفقود.",
  },
};

function getLang(searchParams) {
  const langParam = typeof searchParams?.get === "function" ? searchParams.get("lang") : null;
  if (langParam === "ar" || langParam === "en") return langParam;
  if (typeof navigator !== "undefined" && navigator.language && navigator.language.startsWith("ar"))
    return "ar";
  return "en";
}

function getDashboardSignInUrl() {
  const base =
    process.env.REACT_APP_DASHBOARD_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return base ? `${String(base).replace(/\/$/, "")}/authentication/sign-in` : null;
}

function getClientSiteLoginUrl() {
  const base = process.env.REACT_APP_FRONTEND_URL || "";
  return base ? `${base.replace(/\/$/, "")}/login` : null;
}

export default function VerifyEmail() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const lang = useMemo(() => getLang(searchParams), [searchParams]);
  const t = VERIFY_TEXTS[lang] || VERIFY_TEXTS.en;

  const [status, setStatus] = useState("loading");
  const [messageKey, setMessageKey] = useState("");
  const [verifiedAs, setVerifiedAs] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(REDIRECT_DELAY_SEC);
  const redirectTriggered = useRef(false);

  const message = messageKey ? t[messageKey] || messageKey : "";

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessageKey("errorMissingToken");
      return;
    }

    const verify = async () => {
      try {
        await apiRequest(`/clients/verify/${token}`, { method: "GET" });
        setMessageKey("successClient");
        setVerifiedAs("client");
        setStatus("success");
        return;
      } catch (clientErr) {
        const isClientNotFound = clientErr.status === 400 || clientErr.status === 404;
        if (isClientNotFound) {
          try {
            await apiRequest(`/property-owner/verify/${token}`, { method: "GET" });
            setMessageKey("successOwner");
            setVerifiedAs("property_owner");
            setStatus("success");
            return;
          } catch (ownerErr) {
            setMessageKey("errorInvalidLink");
            setStatus("error");
            return;
          }
        }
        setMessageKey("errorInvalidLink");
        setStatus("error");
      }
    };

    verify();
  }, [token]);

  useEffect(() => {
    if (status !== "success" || verifiedAs == null || redirectTriggered.current) return;

    const redirectUrl =
      verifiedAs === "property_owner" ? getDashboardSignInUrl() : getClientSiteLoginUrl();

    if (!redirectUrl) {
      setRedirectCountdown(-1);
      return;
    }

    const tick = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(tick);
          if (!redirectTriggered.current) {
            redirectTriggered.current = true;
            window.location.href = redirectUrl;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [status, verifiedAs]);

  const buttonSx = {
    background: BRAND_BUTTON,
    color: "#fff",
    "&:hover": { background: BRAND_BUTTON_HOVER },
    "&:disabled": { background: "#8a7358", color: "rgba(255,255,255,0.7)" },
  };

  return (
    <BasicLayout>
      <Card
        sx={{
          overflow: "visible",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header – same as sign-in */}
        <MDBox
          borderRadius="lg"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={{
            background: BRAND_GOLD_HEADER,
            boxShadow: "none",
          }}
        >
          <MDBox
            component="img"
            src={logo}
            alt="HostA Global"
            sx={{
              width: "100%",
              maxWidth: 220,
              height: "auto",
              objectFit: "contain",
              borderRadius: "5px",
              mt: 1,
              mb: 1,
              mixBlendMode: "multiply",
              filter: "contrast(1.05)",
            }}
          />
          <MDTypography variant="body2" sx={{ color: "#e8e8e8" }} mt={0.5} mb={1}>
            Property Rental Management System
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }} mt={0.5} mb={1}>
            Administration Dashboard
          </MDTypography>
          <MDTypography variant="h6" fontWeight="regular" sx={{ color: "#fff" }} mt={2}>
            {t.headerTitle}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {status === "loading" && (
            <MDBox textAlign="center" py={4}>
              <CircularProgress size={40} sx={{ color: BRAND_BUTTON, mb: 2 }} />
              <MDTypography variant="body2" color="text">
                {t.verifying}
              </MDTypography>
            </MDBox>
          )}

          {status === "success" && (
            <>
              <MDBox
                p={2}
                borderRadius="md"
                sx={{
                  bgcolor: "rgba(76, 175, 80, 0.15)",
                  border: "1px solid rgba(76, 175, 80, 0.5)",
                  color: "text.main",
                }}
                textAlign="center"
                mb={2}
              >
                <MDTypography variant="body1" fontWeight="medium" color="success.main">
                  {message}
                </MDTypography>
              </MDBox>
              {verifiedAs === "property_owner" && (
                <MDTypography variant="body2" color="text" textAlign="center" mb={1}>
                  {t.partnerDashboardSub}
                </MDTypography>
              )}
              {verifiedAs === "client" && (
                <MDTypography variant="body2" color="text" textAlign="center" mb={1}>
                  {t.websiteSub}
                </MDTypography>
              )}
              {redirectCountdown > 0 && (
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  mb={2}
                >
                  {t.redirecting
                    .replace("{count}", String(redirectCountdown))
                    .replace("{s}", lang === "en" && redirectCountdown !== 1 ? "s" : "")}
                </MDTypography>
              )}
              {verifiedAs === "property_owner" && getDashboardSignInUrl() && (
                <MDButton fullWidth href={getDashboardSignInUrl()} sx={{ ...buttonSx, mt: 2 }}>
                  {t.goPartner}
                </MDButton>
              )}
              {verifiedAs === "client" && getClientSiteLoginUrl() && (
                <MDButton fullWidth href={getClientSiteLoginUrl()} sx={{ ...buttonSx, mt: 2 }}>
                  {t.goWebsite}
                </MDButton>
              )}
            </>
          )}

          {status === "error" && (
            <>
              <MDBox
                p={2}
                borderRadius="md"
                sx={{
                  bgcolor: "rgba(211, 47, 47, 0.12)",
                  border: "1px solid rgba(211, 47, 47, 0.5)",
                }}
                textAlign="center"
                mb={2}
              >
                <MDTypography variant="body2" color="error.main">
                  {message}
                </MDTypography>
              </MDBox>
              <MDButton
                fullWidth
                href={getDashboardSignInUrl() || "/authentication/sign-in"}
                sx={buttonSx}
              >
                {t.goSignIn}
              </MDButton>
            </>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
}
